import type { GlobalFileId, MonospaceWorkspace } from '#layers/data-model/public';
import { createEventHook } from '@vueuse/core';
import { computed } from 'vue';
import { createError } from '#app/composables/error';
import { useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { useNuxtApp } from '#app/nuxt';
import { defineRstoreModule } from '#imports';
import { createRequestTracker, ErrorCodes, getErrorStatusCode } from '#layers/base/public';
import { useWorkspaceSpaceRoutes } from '#layers/spaces/public';
import { SINGLETON_KEY } from '#layers/workspace/rstore/utils/singleton';

interface WorkspaceCreateInput {
	displayName: string;
	apiName: string;
}

interface WorkspaceChangedHookPayload {
	oldWorkspaceId: string | null;
	newWorkspaceId: string | null;
}

function translate(key: string) {
	return useNuxtApp().$i18n.t(key);
}

export const useWorkspaceStore = defineRstoreModule('monospace-studio-workspace', ({ store, defineState, defineMutation }) => {
	const appConfig = useAppConfig();
	const router = useRouter();
	const { loadWorkspaceSpaceRoutes, removeWorkspaceSpaceRoutes, updateWorkspaceSpaceRoutes } = useWorkspaceSpaceRoutes();

	const beforeWorkspaceChangedHook = createEventHook<WorkspaceChangedHookPayload>();
	const workspaceChangedHook = createEventHook<WorkspaceChangedHookPayload>();

	const hydrationRequests = createRequestTracker();

	const state = defineState({
		currentWorkspaceSlug: null as string | null,
	});

	const currentWorkspaceQuery = store.MonospaceWorkspace.query((q) => q.first(state.currentWorkspaceSlug
		? ({
				key: state.currentWorkspaceSlug,
			})
		: ({
				enabled: false,
			})));

	const { data: currentWorkspace } = currentWorkspaceQuery;

	const createWorkspace = defineMutation((form: WorkspaceCreateInput) => store.MonospaceWorkspace.create(form));

	// The create endpoint only accepts name/apiName, so a logo chosen during
	// creation is applied as a follow-up update keyed by the workspace slug.
	const setWorkspaceLogo = defineMutation((slug: string, logoId: GlobalFileId | null) =>
		store.MonospaceWorkspace.update({ logoId }, { key: slug }));

	return {
		currentWorkspace,
		currentWorkspaceSlug: computed(() => state.currentWorkspaceSlug),
		createWorkspace,
		setWorkspaceLogo,
		hydrateWorkspace,
		scheduleDeferredUnload,
		onBeforeWorkspaceChanged: beforeWorkspaceChangedHook.on,
		onWorkspaceChanged: workspaceChangedHook.on,
	};

	async function hydrateWorkspace(workspaceSlug: string, opts: { fromWorkspaceRoute: boolean }) {
		// Phase 1: Invalidate any scheduled deferred unload by bumping the version.
		// This must happen before the early return, so same-workspace calls still cancel pending teardowns.
		hydrationRequests.invalidate();

		if (state.currentWorkspaceSlug === workspaceSlug) return;

		// Phase 2: Bump again and capture for staleness detection - if another hydrateWorkspace()
		// call arrives while we're awaiting, the captured version goes stale, and we bail out and only go through with
		// the new workspace from the second, competing call.
		const request = hydrationRequests.start();

		if (state.currentWorkspaceSlug && appConfig.monospace.workspaceSlug) {
			await removeCurrentWorkspaceRoutes();
		}

		try {
			const workspace = await store.MonospaceWorkspace.findFirst({
				filter: { apiName: { _eq: workspaceSlug } },
			});

			if (!workspace) {
				await setCurrentWorkspace(null);

				throw createError({
					statusCode: 404,
					statusMessage: translate('workspace.not_found'),
				});
			}

			if (!request.isCurrent()) return;

			await setCurrentWorkspace(workspace);

			await store.MonospaceSettings.findFirst({
				key: SINGLETON_KEY,
				fetchPolicy: 'fetch-only',
			});

			if (!request.isCurrent()) return;

			const spaces = await loadWorkspaceSpaceRoutes();

			if (!request.isCurrent()) return;

			updateWorkspaceSpaceRoutes({
				spaces,
				skipCurrentRouteUpdate: !opts.fromWorkspaceRoute,
			});

			// TODO filter on current user client-side or server-side
			await store.MonospaceKeyStorage.query((q) => q.many());
		}
		catch (error) {
			if (getErrorStatusCode(error) === 403) {
				throw createError({
					statusCode: 403,
					statusMessage: translate('workspace.no_access'),
					data: { code: ErrorCodes.WORKSPACE_ACCESS_DENIED },
				});
			}

			throw error;
		}
	}

	async function removeCurrentWorkspaceRoutes() {
		await removeWorkspaceSpaceRoutes();
	}

	function scheduleDeferredUnload() {
		const request = hydrationRequests.capture();

		const unregister = router.afterEach(() => {
			unregister();

			if (!request.isCurrent()) return;

			setCurrentWorkspace(null);
		});
	}

	async function setCurrentWorkspace(workspace: MonospaceWorkspace | null) {
		const newId = workspace?.id ?? null;
		const oldId = currentWorkspace.value?.id ?? null;

		if (newId === oldId) return;

		const request = hydrationRequests.capture();

		await beforeWorkspaceChangedHook.trigger({ oldWorkspaceId: oldId, newWorkspaceId: newId });

		// Abort stale unload — a new hydration started while hooks were running
		if (workspace === null && !request.isCurrent()) return;

		state.currentWorkspaceSlug = workspace?.apiName ?? null;
		appConfig.monospace.workspaceSlug = workspace?.apiName;

		await workspaceChangedHook.trigger({ oldWorkspaceId: oldId, newWorkspaceId: newId });
	}
});
